import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  GrantsAndAllowedApis,
  Language,
  Provider,
  QueryAndOutputs,
  TableSchema,
  TokenAllowedApis,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowComponentValue,
  WorkflowParameterValueError,
  WorkflowParameterValueErrorType
} from '../types'
import { checkGreaterOrEqualATVersion, isExpressionValue } from '../utils'
import { addColumns, removeColumn } from '../utils/schemas'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class CreateRoutes extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.createroutes',
        group: 'Spatial Constructors',
        title: 'Create Routes',
        description: `
        This component creates routes between origin and destination points stored in a table.
        `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'origin',
          type: WorkflowComponentParamType.Column,
          title: 'Origin column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'destination',
          type: WorkflowComponentParamType.Column,
          title: 'Destination column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'newcol',
          type: WorkflowComponentParamType.String,
          title: 'Name for column with routes',
          default: 'geom',
          optional: true,
          allowExpressions: false
        },
        {
          name: 'mode',
          type: WorkflowComponentParamType.Selection,
          title: 'Type of transport',
          options: ['car', 'pedestrian', 'bicycle', 'truck', 'taxi', 'bus']
        },
        {
          name: 'customoptions',
          type: WorkflowComponentParamType.String,
          title: 'Routing options',
          default: '',
          optional: true,
          mode: WorkflowComponentParameterMode.Multiline,
          allowExpressions: false
        }
      ]
    )
    this.supportedProviders = [
      Provider.BigQuery,
      Provider.Snowflake,
      Provider.Redshift,
      Provider.DatabricksRest
    ]
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const storedProcedure = codeType === WorkflowCodeType.StoredProcedure
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const preparedParams = this._prepareParameters(params)
    let query = ''

    const source = connection.quoteName(preparedParams.source as string)
    const cols = ['*']
    const originColumn = preparedParams.origin as string
    const hasOriginColumn = originColumn.toLowerCase() === 'origin'
    if (!hasOriginColumn) {
      cols.push(`${preparedParams.origin} AS origin`)
    }

    const destinationColumn = preparedParams.destination as string
    const hasDestinationColumn =
      destinationColumn.toLowerCase() === 'destination'
    if (!hasDestinationColumn) {
      cols.push(`${preparedParams.destination} AS destination`)
    }

    const nullOptions =
      connection.type === Provider.DatabricksRest ? "''" : 'NULL'
    const options = preparedParams.customoptions
      ? `'${JSON.stringify(
          JSON.parse(preparedParams.customoptions as string)
        )}'`
      : nullOptions
    if (connection.type === Provider.BigQuery) {
      query = dedent`
          ${
            storedProcedure
              ? dedent`
                DECLARE tempOutputTable STRING DEFAULT '${connection.quoteName(
                  connection.tempLocation
                )}.' || REPLACE(GENERATE_UUID(), '-', '_');
                BEGIN
                `
              : ''
          }
          CALL ${connection.quoteName(
            envVars.analyticsToolboxDataset as string
          )}.CREATE_ROUTES(
            '${envVars.apiBaseUrl}',
            '${envVars.accessToken}',
            'SELECT ${cols.join(', ')} FROM ${source}',
            ${storedProcedure ? 'tempOutputTable' : `'${tablename}'`},
            '${preparedParams.newcol}',
            '${preparedParams.mode}',
            ${options});
          ${
            storedProcedure
              ? dedent`
                  EXECUTE IMMEDIATE '''
                    CREATE TEMPORARY TABLE ${connection.quoteName(tablename)} AS
                      SELECT *
                      FROM ''' || tempOutputTable;
                  END;`
              : ''
          }
          `
    } else if (connection.type === Provider.Snowflake) {
      const ldsParams =
        envVars.isCartoDW ||
        checkGreaterOrEqualATVersion('1.6.0', envVars.analyticsToolboxVersion)
          ? `,'${envVars.apiBaseUrl}','${envVars.accessToken}'`
          : ''
      query = dedent`
          ${
            storedProcedure
              ? dedent`
                DECLARE
                  tempOutputTable TEXT DEFAULT '${connection.quoteName(
                    connection.tempLocation
                  )}.outputtable' || REPLACE(UUID_STRING(), '-', '_');
                BEGIN
                `
              : ''
          }
          CALL ${connection.quoteName(
            envVars.analyticsToolboxDataset as string
          )}.CREATE_ROUTES(
            'SELECT ${cols.join(', ')} FROM ${source}',
            ${storedProcedure ? 'tempOutputTable' : `'${tablename}'`},
            '${preparedParams.newcol}',
            '${preparedParams.mode}',
            ${options}${ldsParams});
          ${
            storedProcedure
              ? dedent`
                EXECUTE IMMEDIATE '
                  CREATE TEMPORARY TABLE ${connection.quoteName(tablename)} AS
                    SELECT *
                    FROM ' || tempOutputTable;
                END;`
              : ''
          }
          `
    } else if (connection.type === Provider.Redshift) {
      const ldsParams =
        envVars.isCartoDW ||
        checkGreaterOrEqualATVersion('1.3.0', envVars.analyticsToolboxVersion)
          ? `,'${envVars.apiBaseUrl}','${envVars.accessToken}'`
          : ''
      query = dedent`
          ${
            storedProcedure
              ? dedent`DECLARE
                    outputTableName VARCHAR(MAX) DEFAULT '${connection.quoteName(
                      connection.tempLocation
                    )}.outputtable' || REPLACE(EXTRACT(EPOCH FROM CURRENT_TIME)::VARCHAR, '.', '');
                  BEGIN
                  `
              : dedent`DECLARE
                  outputTableName VARCHAR(MAX):= '${tablename}';
                BEGIN`
          }
          CALL ${connection.quoteName(
            envVars.analyticsToolboxDataset as string
          )}.CREATE_ROUTES(
            'SELECT ${cols.join(', ')} FROM ${source}',
            outputTableName,
            '${preparedParams.newcol}',
            '${preparedParams.mode}',
            ${options}${ldsParams});
          ${
            storedProcedure
              ? dedent`
                EXECUTE '
                  CREATE TEMPORARY TABLE ${connection.quoteName(tablename)} AS
                    SELECT *
                    FROM ' || outputTableName;`
              : ''
          }
          END;
          `
    } else if (connection.type === Provider.DatabricksRest) {
      const temptablename = connection.getOutputTableName(
        workflow.id,
        componentName,
        params,
        'temp',
        codeType
      )
      query = dedent`
          spark.sql('''DROP TABLE IF EXISTS ${connection.quoteName(
            temptablename
          )}''')
          spark.read.format("com.carto.analytics.toolbox.stored.procedures.ATStoredProcedureDatasource").option(
                "sql",
                """
                CALL_CARTO carto_un.carto.CREATE_ROUTES(
                  '${envVars.apiBaseUrl}',
                  '${envVars.accessToken}',
                  'SELECT ${cols.join(', ')} FROM ${connection.quoteName(
        preparedParams.source as string
      )}',
                  '${connection.quoteName(temptablename)}',
                  '${preparedParams.newcol}',
                  '${preparedParams.mode}',
                  ${options}
                  );
                """
              ).load().collect();
          ${(
            connection as DBricksConnection
          ).wrapCodeInPrepareForVisualizationBlock(
            connection.quoteName(temptablename),
            connection.quoteName(tablename),
            preparedParams.newcol as string,
            codeType
          )}
        `
    }
    query = connection.wrapCodeInIfTableExistsBlock(
      query,
      [tablename],
      codeType
    )

    return {
      outputs: { result: tablename },
      query
    }
  }

  getRequiredGrants(): GrantsAndAllowedApis {
    return {
      grants: [],
      apis: [TokenAllowedApis.lds]
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.BigQuery:
        return '1.4.0'
      case Provider.Snowflake:
        return '1.6.0'
      case Provider.Redshift:
        return '1.3.0'
      default:
        return null
    }
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  getVersion(provider: Provider): string {
    return '1'
  }

  checkInputValues(
    params: WorkflowComponentCheckInputValuesParams
  ): WorkflowParameterValueError[] {
    const errors = super.checkInputValues(params)

    const { resolvedParamValues, schemas } = params
    const preparedParams = this._prepareParameters(resolvedParamValues)

    if (preparedParams.origin === preparedParams.destination) {
      errors.push({
        parameter: 'destination',
        type: WorkflowParameterValueErrorType.WrongValue,
        message: `The destination cannot be the same as the origin`
      })
    }

    if (
      resolvedParamValues.customoptions &&
      !isExpressionValue(params.params.customoptions as WorkflowComponentValue)
    ) {
      try {
        const json = JSON.parse(resolvedParamValues.customoptions as string)
      } catch (e) {
        errors.push({
          parameter: 'customoptions',
          type: WorkflowParameterValueErrorType.WrongValue,
          message: 'Invalid JSON string'
        })
      }
    }

    return errors
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({
    columns,
    params,
    connection
  }: GetOutputSchemaParams): TableSchema {
    const preparedParams = this._prepareParameters(params)
    return addColumns(removeColumn(columns.source, params.geo as string), [
      {
        name: preparedParams.newcol as string,
        type: connection.getGeoDataType()
      },
      {
        name: 'carto_routing_metadata',
        type: ColumnDataType.String
      },
      ...(connection.type === Provider.DatabricksRest
        ? (connection as DBricksConnection).BOUNDS_COLUMNS
        : [])
    ])
  }
}

export { CreateRoutes }
